import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { Sheet, SheetButton, SheetRow } from '../../components/Sheet';
import { AutoGrowTextarea, DiscardChangesSheet, TemplatePreviewSheet } from '../../components/automationsMobile';
import { useIsMobile } from '../../lib/useIsMobile';
import { insertPlaceholder, placeholderTag } from '../../lib/automationTemplate';
import { draftDirty, htmlToPlainText, SAMPLE_FROM, savedDraft, templateTabLabel, unrecognisedPlaceholders } from './templateHelpers';
import type { Draft, TemplatePreview, TemplateRow } from './types';

// ── Template editor (§3) — full-viewport takeover (Henry-approved Design 1,
//    docs/mockups-template-editor-2026-07.html): subject + body, click-to-
//    insert placeholder chips, a debounced live preview rendered server-side
//    and shown at true 600px width in a sandboxed iframe (Desktop) or as
//    plain text (Plain-text), and reset-to-default. Replaces the old
//    880px Modal, which Henry rejected after the deploy walkthrough as too
//    cramped to read a real email in. ──────────────────────────────────────

export function TemplateEditorFullCanvas({
  automationTitle,
  initialTemplates,
  onSaved,
  onClose,
}: {
  automationTitle: string;
  initialTemplates: TemplateRow[];
  onSaved: () => void;
  onClose: () => void;
}) {
  const [templates, setTemplates] = useState<TemplateRow[]>(initialTemplates);
  const [activeKey, setActiveKey] = useState<string | null>(initialTemplates[0]?.template_key ?? null);
  const active = templates.find((t) => t.template_key === activeKey) ?? null;

  // Per-stage drafts, keyed by template_key — switching stages never loses
  // an in-progress edit (my call on the handoff's "prompt or discard
  // consistently": nothing here needs a prompt because nothing is ever
  // discarded until Save/Reset/close). Only Save/close touch server state.
  const [drafts, setDrafts] = useState<Record<string, Draft>>(() =>
    Object.fromEntries(initialTemplates.map((t) => [t.template_key, savedDraft(t)])),
  );
  const activeDraft: Draft = (activeKey && drafts[activeKey]) || { subject: '', body: '' };

  const [previewMode, setPreviewMode] = useState<'desktop' | 'plaintext'>('desktop');
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [previewBroken, setPreviewBroken] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Mobile only: the preview is a second sheet, and Cancel with unsaved
  // edits asks in a sheet rather than a window.confirm (§0 — no browser
  // dialogs on mobile).
  const isMobile = useIsMobile();
  const [showPreview, setShowPreview] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const lastFocused = useRef<'subject' | 'body'>('body');
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const updateDraft = (patch: Partial<Draft>) => {
    if (!activeKey) return;
    setDrafts((d) => ({ ...d, [activeKey]: { ...(d[activeKey] ?? { subject: '', body: '' }), ...patch } }));
  };

  // Debounced live preview against the active stage's draft — degrades to a
  // plain-text fallback (the raw draft body) if the preview endpoint 404s.
  useEffect(() => {
    if (!active || previewBroken) return;
    const t = setTimeout(() => {
      api<TemplatePreview>(`/api/admin/automations/templates/${active.template_key}/preview`, {
        method: 'POST',
        json: { subject: activeDraft.subject, body: activeDraft.body },
      })
        .then(setPreview)
        .catch(() => setPreviewBroken(true));
    }, 500);
    return () => clearTimeout(t);
  }, [active, activeDraft.subject, activeDraft.body, previewBroken]);

  const anyDirty = templates.some((t) => draftDirty(t, drafts[t.template_key]));

  const requestClose = () => {
    if (!anyDirty) {
      onClose();
      return;
    }
    if (isMobile) {
      setConfirmDiscard(true);
      return;
    }
    if (!window.confirm('Discard unsaved changes to this template?')) return;
    onClose();
  };

  // ESC closes (with the same confirm-if-unsaved as the back link/Cancel) —
  // attached for the lifetime of this component, i.e. exactly while the
  // full-canvas takeover is mounted.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!active) {
    if (isMobile) {
      return (
        <Sheet
          open
          onClose={onClose}
          variant="half"
          title={automationTitle}
          context="Template"
          rightAction={<SheetButton onClick={onClose}>Close</SheetButton>}
        >
          <div className="atm-m-hempty">No templates found for this automation.</div>
        </Sheet>
      );
    }
    return (
      <div className="tplfc-overlay" role="dialog" aria-modal="true">
        <div className="tplfc-top">
          <button type="button" className="tplfc-back" onClick={onClose}>
            ‹ Automations
          </button>
          <span className="tplfc-family">{automationTitle}</span>
          <div className="tplfc-top-right">
            <span className="tplfc-esc">ESC</span>
          </div>
        </div>
        <div className="tplfc-empty">No templates found for this automation.</div>
        <div className="tplfc-foot">
          <span style={{ marginLeft: 'auto' }} />
          <button className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  const hasOverride = active.subject_override !== null || active.body_override !== null;
  const dirty = draftDirty(active, activeDraft);
  const liveWarnings = unrecognisedPlaceholders(active.placeholders, activeDraft.subject, activeDraft.body);

  const insertChip = (raw: string) => {
    const toSubject = lastFocused.current === 'subject' && subjectRef.current !== null;
    const el: HTMLInputElement | HTMLTextAreaElement | null = toSubject ? subjectRef.current : bodyRef.current;
    if (!el) return;
    const next = insertPlaceholder(toSubject ? activeDraft.subject : activeDraft.body, el.selectionStart, raw);
    updateDraft(toSubject ? { subject: next.text } : { body: next.text });
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(next.caret, next.caret);
    });
  };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ template: TemplateRow; warnings: string[] }>(
        `/api/admin/automations/templates/${active.template_key}`,
        { method: 'PUT', json: { subject: activeDraft.subject, body: activeDraft.body } },
      );
      setTemplates((list) => list.map((t) => (t.template_key === active.template_key ? res.template : t)));
      setDrafts((d) => ({ ...d, [active.template_key]: savedDraft(res.template) }));
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template.');
    } finally {
      setBusy(false);
    }
  };

  const resetDefault = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/admin/automations/templates/${active.template_key}`, { method: 'DELETE' });
      setTemplates((list) =>
        list.map((t) => (t.template_key === active.template_key ? { ...t, subject_override: null, body_override: null } : t)),
      );
      setDrafts((d) => ({ ...d, [active.template_key]: { subject: active.subject_default, body: active.body_default } }));
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset template.');
    } finally {
      setBusy(false);
    }
  };

  // ≤880px (mockup §14.1 C): the full-canvas takeover becomes a full sheet —
  // Cancel / Save in the header, stage chips pinned under it, the preview
  // behind its own sheet instead of a side-by-side column.
  if (isMobile) {
    const previewHtml = preview && !previewBroken ? preview.html : null;
    return (
      <>
        <Sheet
          open
          onClose={requestClose}
          variant="full"
          title={automationTitle}
          context={anyDirty ? 'Template · unsaved changes' : 'Template'}
          className="atm-m-tpl"
          leftAction={<SheetButton onClick={requestClose}>Cancel</SheetButton>}
          rightAction={
            <SheetButton tone="accent" onClick={() => void save()} disabled={!dirty || busy}>
              Save
            </SheetButton>
          }
          pinned={
            templates.length > 1 ? (
              <div className="mtabs atm-m-tpl-tabs">
                {templates.map((t) => (
                  <button
                    key={t.template_key}
                    type="button"
                    className={`t${t.template_key === activeKey ? ' on' : ''}`}
                    onClick={() => setActiveKey(t.template_key)}
                  >
                    {templateTabLabel(t)}
                  </button>
                ))}
              </div>
            ) : undefined
          }
        >
          <div className="atm-m-tpl-body">
            <div className="f-field">
              <span className="f-label">Subject</span>
              <input
                ref={subjectRef}
                className="f-input"
                value={activeDraft.subject}
                onFocus={() => {
                  lastFocused.current = 'subject';
                }}
                onChange={(e) => updateDraft({ subject: e.target.value })}
              />
            </div>

            {active.placeholders.length > 0 && (
              <div className="f-field">
                <span className="f-label">Insert placeholder</span>
                <div className="chiprow atm-m-tpl-chips">
                  {active.placeholders.map((p) => (
                    <button key={p} type="button" className="c" onClick={() => insertChip(p)}>
                      {placeholderTag(p)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="f-field">
              <span className="f-label">Body — plain text</span>
              <AutoGrowTextarea
                innerRef={bodyRef}
                className="f-textarea atm-m-tpl-textarea"
                value={activeDraft.body}
                onFocus={() => {
                  lastFocused.current = 'body';
                }}
                onChange={(body) => updateDraft({ body })}
              />
            </div>

            <p className="atm-m-tpl-help">
              The app keeps HTML structure, escaping and the footer/preferences link — this box is
              plain text with placeholders only. Unrecognised placeholders render blank.
            </p>
            {liveWarnings.length > 0 && (
              <p className="atm-m-tpl-help warn">
                ⚠ Unrecognised placeholder{liveWarnings.length > 1 ? 's' : ''}: {liveWarnings.join(', ')} — not one
                of this template's tags, so {liveWarnings.length > 1 ? "they'll" : "it'll"} render blank. Doesn't
                block saving.
              </p>
            )}
            {error && <p className="error-text atm-m-tpl-error">{error}</p>}
          </div>

          <SheetRow label="Preview with sample data" right="›" onSelect={() => setShowPreview(true)} />
          <SheetRow
            label="Reset to default"
            tone="dim"
            disabled={busy || !hasOverride}
            onSelect={() => void resetDefault()}
          />
        </Sheet>

        <TemplatePreviewSheet
          open={showPreview}
          onClose={() => setShowPreview(false)}
          mode={previewMode}
          onMode={setPreviewMode}
          from={SAMPLE_FROM}
          to={preview?.to_email ?? 'sample recipient'}
          sampleLabel={preview?.sample_label}
          subject={preview?.subject ?? activeDraft.subject}
          html={previewHtml}
          plainText={previewHtml === null ? activeDraft.body : htmlToPlainText(previewHtml)}
        />

        <DiscardChangesSheet
          open={confirmDiscard}
          context={automationTitle}
          onKeep={() => setConfirmDiscard(false)}
          onDiscard={() => {
            setConfirmDiscard(false);
            onClose();
          }}
        />
      </>
    );
  }

  return (
    <div className="tplfc-overlay" role="dialog" aria-modal="true">
      <div className="tplfc-top">
        <button type="button" className="tplfc-back" onClick={requestClose}>
          ‹ Automations
        </button>
        <span className="tplfc-family">{automationTitle}</span>
        {templates.length > 1 && (
          <div className="tplfc-tabs">
            {templates.map((t) => (
              <button
                key={t.template_key}
                type="button"
                className={`tplfc-tab${t.template_key === activeKey ? ' active' : ''}`}
                onClick={() => setActiveKey(t.template_key)}
              >
                {templateTabLabel(t)}
              </button>
            ))}
          </div>
        )}
        <div className="tplfc-top-right">
          {anyDirty && (
            <span className="tplfc-dirty">
              <span className="dot" />
              Unsaved changes
            </span>
          )}
          <span className="tplfc-esc">ESC</span>
        </div>
      </div>

      <div className="tplfc-body">
        <div className="tplfc-edit">
          <div className="f-field">
            <span className="f-label">Subject</span>
            <input
              ref={subjectRef}
              className="f-input"
              value={activeDraft.subject}
              onFocus={() => {
                lastFocused.current = 'subject';
              }}
              onChange={(e) => updateDraft({ subject: e.target.value })}
            />
          </div>

          {active.placeholders.length > 0 && (
            <>
              <div className="tplfc-chips-label">Insert placeholder</div>
              <div className="tplfc-chips">
                {active.placeholders.map((p) => (
                  <span key={p} className="chip" onClick={() => insertChip(p)}>
                    {p.startsWith('{{') ? p : `{{${p}}}`}
                  </span>
                ))}
              </div>
            </>
          )}

          <div className="f-field" style={{ marginTop: 18 }}>
            <span className="f-label">Body — plain text</span>
            <textarea
              ref={bodyRef}
              className="f-textarea tplfc-textarea"
              value={activeDraft.body}
              onFocus={() => {
                lastFocused.current = 'body';
              }}
              onChange={(e) => updateDraft({ body: e.target.value })}
            />
          </div>

          <p className="tplfc-help">
            The app keeps HTML structure, escaping and the footer/preferences link — this box is plain text with
            placeholders only. Unrecognised placeholders render blank.
          </p>
          {liveWarnings.length > 0 && (
            <p className="tplfc-help tplfc-help-warn">
              ⚠ Unrecognised placeholder{liveWarnings.length > 1 ? 's' : ''}: {liveWarnings.join(', ')} — not one of
              this template's tags, so {liveWarnings.length > 1 ? "they'll" : "it'll"} render blank. Doesn't block
              saving.
            </p>
          )}
          {error && (
            <p className="error-text" style={{ marginTop: 8 }}>
              {error}
            </p>
          )}
        </div>

        <div className="tplfc-divider" />

        <div className="tplfc-preview-col">
          <div className="tplfc-preview-head">
            <span className="tplfc-preview-label">Live preview · sample data</span>
            <div className="tplfc-toggle">
              <button
                type="button"
                className={previewMode === 'desktop' ? 'active' : ''}
                onClick={() => setPreviewMode('desktop')}
              >
                Desktop
              </button>
              <button
                type="button"
                className={previewMode === 'plaintext' ? 'active' : ''}
                onClick={() => setPreviewMode('plaintext')}
              >
                Plain-text
              </button>
            </div>
          </div>

          {previewMode === 'desktop' ? (
            <div className="tplfc-email-win">
              <div className="tplfc-email-toolbar">
                <span>
                  <b>From:</b> {SAMPLE_FROM}
                </span>
                <span>
                  <b>To:</b> {preview?.to_email ?? 'sample recipient'}
                </span>
                {preview?.sample_label && <span className="sample">Sample: {preview.sample_label}</span>}
              </div>
              <div className="tplfc-email-subject">{preview?.subject ?? activeDraft.subject}</div>
              {preview && !previewBroken ? (
                <iframe className="tplfc-email-frame" srcDoc={preview.html} sandbox="" title="Template preview" />
              ) : (
                <div className="tplfc-email-fallback-body">{activeDraft.body}</div>
              )}
            </div>
          ) : (
            <div className="tplfc-plaintext">
              {preview && !previewBroken ? htmlToPlainText(preview.html) : activeDraft.body}
            </div>
          )}
        </div>
      </div>

      <div className="tplfc-foot">
        <button className="btn" onClick={resetDefault} disabled={busy || !hasOverride}>
          Reset to default
        </button>
        <span style={{ flex: 1 }} />
        {liveWarnings.length > 0 && (
          <span className="tplfc-warn-badge">
            ⚠ {liveWarnings.length} warning{liveWarnings.length > 1 ? 's' : ''}
          </span>
        )}
        <button className="btn" onClick={requestClose}>
          Cancel
        </button>
        <button className="btn primary" onClick={save} disabled={!dirty || busy}>
          Save template →
        </button>
      </div>
    </div>
  );
}
